import React, { useEffect, useState } from 'react';
import { MapPin, Users, PackageCheck, ShieldCheck, ArrowUpRight } from 'lucide-react';
import api from '../../services/api';
import mapImg from '../../assets/cuba-network.webp';

const GREEN = '#4ade80';

// Posiciones de los pins horneados en la imagen (1860x845), en porcentaje.
export const HOTSPOTS = [
  { name: 'La Habana',           x: 17.8, y: 12.1 },
  { name: 'Pinar del Río',       x: 8.2,  y: 25.8 },
  { name: 'Artemisa',            x: 19.1, y: 22.5 },
  { name: 'Mayabeque',           x: 26.4, y: 19.9 },
  { name: 'Matanzas',            x: 35.8, y: 21.5 },
  { name: 'Villa Clara',         x: 46.1, y: 25.6 },
  { name: 'Cienfuegos',          x: 35.3, y: 34.0 },
  { name: 'Sancti Spíritus',     x: 53.6, y: 34.1 },
  { name: 'Ciego de Ávila',      x: 61.3, y: 40.4 },
  { name: 'Camagüey',            x: 72.4, y: 48.2 },
  { name: 'Las Tunas',           x: 66.2, y: 59.5 },
  { name: 'Holguín',             x: 84.8, y: 54.2 },
  { name: 'Granma',              x: 67.5, y: 70.8 },
  { name: 'Santiago de Cuba',    x: 76.5, y: 71.0 },
  { name: 'Guantánamo',          x: 91.1, y: 68.4 },
  { name: 'Isla de la Juventud', x: 13.7, y: 52.2 },
];

export default function CubaNetworkMap() {
  const [active, setActive] = useState('La Habana');
  const [counts, setCounts] = useState({});

  useEffect(() => {
    api.get('/api/v1/marketplace/agents')
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.agents || []);
        const c = {};
        list.forEach(a => { const p = a.province; if (p) c[p] = (c[p] || 0) + 1; });
        setCounts(c);
      })
      .catch(() => setCounts({}));
  }, []);

  const selCount = counts[active] || 0;
  const totalAgents = Object.values(counts).reduce((s, n) => s + n, 0);
  const provincesCovered = Object.keys(counts).length;

  const stats = [
    { Icon: Users,       big: provincesCovered > 0 ? provincesCovered : '15', label: 'Provincias', sub: provincesCovered > 0 ? 'con cobertura activa' : 'Cobertura nacional' },
    { Icon: MapPin,      big: totalAgents > 0 ? totalAgents : '—', label: 'Agentes verificados', sub: 'En toda Cuba' },
    { Icon: PackageCheck, big: '100%', label: 'Entregas a la puerta', sub: 'Con prueba de entrega' },
    { Icon: ShieldCheck, big: 'Escrow', label: 'Seguridad', sub: 'En cada remesa' },
  ];

  return (
    <section className="gmap-root">
      <style>{`
        .gmap-root { color: #f1fff6; }
        .gmap-eyebrow { font-size: 10px; letter-spacing: 1.6px; color: ${GREEN}; font-weight: 800; }
        .gmap-root h2 { font-size: clamp(26px, 3vw, 40px); margin: 8px 0 10px; letter-spacing: -1px; }
        .gmap-root h2 em { font-style: normal; color: ${GREEN}; }
        .gmap-lede { font-size: 14px; color: #9fb8ab; max-width: 560px; margin: 0 0 22px; line-height: 1.6; }
        .gmap-frame { position: relative; border-radius: 18px; overflow: hidden; border: 1px solid #162b22; background: #05120f; }
        .gmap-frame img { width: 100%; display: block; }
        .gmap-hot { position: absolute; width: 4.2%; aspect-ratio: 1; transform: translate(-50%, -50%); border-radius: 50%; border: none; background: transparent; cursor: pointer; padding: 0; }
        .gmap-hot:hover, .gmap-hot:focus-visible { box-shadow: 0 0 0 3px rgba(74,222,128,.6); }
        .gmap-hot.sel { box-shadow: 0 0 0 3px ${GREEN}, 0 0 22px rgba(74,222,128,.5); }
        .gmap-stats { position: absolute; left: 2%; right: 22%; bottom: 2.5%; display: flex; gap: 1.6%; }
        .gmap-stat { flex: 1; display: flex; gap: 12px; align-items: center; background: rgba(7,18,14,.92); border: 1px solid #1b3528; border-radius: 14px; padding: 12px 16px; backdrop-filter: blur(4px); }
        .gmap-stat svg { color: ${GREEN}; flex-shrink: 0; }
        .gmap-stat small { display: block; font-size: 10.5px; color: #86a295; }
        .gmap-stat strong { font-size: 19px; line-height: 1.15; }
        .gmap-stats-flow { display: none; }
        .gmap-select { display: flex; justify-content: space-between; align-items: center; gap: 18px; flex-wrap: wrap; margin-top: 16px; background: #0b1b15; border: 1px solid #162b22; border-radius: 16px; padding: 20px 22px; }
        .gmap-select h3 { margin: 6px 0; font-size: 22px; }
        .gmap-select p { margin: 0; font-size: 13px; color: #9fb8ab; }
        .gmap-cta { display: inline-flex; align-items: center; gap: 9px; background: ${GREEN}; color: #06281a; border: none; border-radius: 12px; padding: 13px 22px; font-weight: 800; font-size: 13px; cursor: pointer; text-decoration: none; }
        @media (max-width: 760px) {
          .gmap-stats { display: none; }
          .gmap-stats-flow { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 12px; }
          .gmap-stats-flow .gmap-stat { background: #0b1b15; }
        }
      `}</style>

      <div className="gmap-eyebrow">RED DE ENTREGA · CUBA</div>
      <h2>Una isla. <em>Conectada.</em></h2>
      <p className="gmap-lede">Explora la red de entrega de Guarapo. Toca una provincia para ver su cobertura; la disponibilidad de cada agente se confirma al crear la remesa.</p>

      <div className="gmap-frame">
        <img src={mapImg} alt="Mapa de la red de agentes de Guarapo en Cuba" loading="lazy" />
        {HOTSPOTS.map(p => (
          <button key={p.name}
            className={`gmap-hot${active === p.name ? ' sel' : ''}`}
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
            aria-label={`Seleccionar ${p.name}`}
            onClick={() => setActive(p.name)} />
        ))}
        <div className="gmap-stats">
          {stats.map(({ Icon, big, label, sub }, i) => (
            <div key={i} className="gmap-stat">
              <Icon size={22} />
              <div><small>{label}</small><strong>{big}</strong><small>{sub}</small></div>
            </div>
          ))}
        </div>
      </div>

      <div className="gmap-stats-flow">
        {stats.map(({ Icon, big, label, sub }, i) => (
          <div key={i} className="gmap-stat">
            <Icon size={20} />
            <div><small>{label}</small><strong>{big}</strong><small>{sub}</small></div>
          </div>
        ))}
      </div>

      <div className="gmap-select">
        <div>
          <span className="gmap-eyebrow">PROVINCIA SELECCIONADA</span>
          <h3>{active}</h3>
          <p>{selCount > 0
            ? `${selCount} agente${selCount > 1 ? 's' : ''} verificado${selCount > 1 ? 's' : ''} con cobertura en la provincia.`
            : 'Cobertura en expansión. Regístrate y te avisamos cuando haya agentes disponibles aquí.'}</p>
        </div>
        <a className="gmap-cta" href="/registro">Enviar a {active} <ArrowUpRight size={16} /></a>
      </div>
    </section>
  );
}
